import { useCallback, useEffect } from 'react';
import type { BusinessIssueField } from '@atlassian/jira-business-entity-project/src/services/issue-types-and-fields/types.tsx';
import type { FieldsDetailPreference } from '@atlassian/jira-business-preferences/src/types.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useBusinessEntity } from '@atlassian/jira-business-entity-common/src/controllers/business-entity-context/index.tsx';
import { COMMENT_SUMMARY_TYPE } from '@atlassian/jira-business-constants/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useInlineFieldCreateControls } from '../../inline-field-create-controls/index.tsx';
import type { CreateFieldsType } from '../../inline-field-create-controls/types.tsx';
import { DEFAULT_CUSTOM_FIELDS_BY_TYPE } from '../constants/index.tsx';
import { useFieldsPreference } from '../fields-preference/index.tsx';
import { localStore } from '../../../common/utils/storage.tsx';
import { ENABLED_COMMENTS_FIELD_LOCAL_STORAGE_KEY } from '../../../common/utils/unread-comments-in-list-view-experiment/index.tsx';

type ReorderFieldsProps = {
	visibleFields: BusinessIssueField[];
	oldIndex: number;
	newIndex: number;
};

export type ToggleField = (fieldId: string) => void;
export type ReorderFields = (props: ReorderFieldsProps) => void;

const getFieldsCache = ({
	availableFields,
	fields,
	order,
}: {
	availableFields?: BusinessIssueField[] | null;
	fields: FieldsDetailPreference;
	order: string[];
}): FieldsDetailPreference['details'] | null => {
	if (availableFields == null) {
		return fields.details;
	}
	const newDetails: FieldsDetailPreference['details'] = {};
	availableFields.forEach(({ id, name, type }) => {
		if (order.includes(id)) {
			newDetails[id] = {
				id,
				name,
				type,
			};
		} else if (type != null && order.includes(type)) {
			newDetails[type] = {
				id,
				name,
				type,
			};
		}
	});
	return newDetails;
};

export const useUpdateFieldsDetail = (availableFields?: BusinessIssueField[] | null) => {
	const [fields, setFields] = useFieldsPreference();
	const { data: entity } = useBusinessEntity();

	const enabledCommentsFieldStorageKey = `${entity.id}_${ENABLED_COMMENTS_FIELD_LOCAL_STORAGE_KEY}`;

	useEffect(() => {
		if (availableFields != null) {
			setFields({
				order: fields.order,
				details: getFieldsCache({
					order: fields.order,
					fields,
					availableFields,
				}),
			});
		} else if (fg('_jira_unread_comments_in_list_view_gate')) {
			const hasCommentsFieldBeenPreviouslyEnabled = localStore.get(enabledCommentsFieldStorageKey);

			if (
				!fields.order.includes(COMMENT_SUMMARY_TYPE) &&
				!hasCommentsFieldBeenPreviouslyEnabled &&
				expVal('jira_unread_comments_in_list_view', 'isEnabled', false)
			) {
				const newFieldsOrder = [...fields.order];
				const commentsFieldIndex = fields.order.length > 3 ? 4 : 3;
				newFieldsOrder.splice(commentsFieldIndex, 0, COMMENT_SUMMARY_TYPE);

				setFields({
					order: newFieldsOrder,
					details: fields.details,
				});

				localStore.set(enabledCommentsFieldStorageKey, true);
			}
		}
		// We only want to update cache when available fields change the first time
		// OR when we are adding the comments field for the first time
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availableFields]);
};

export const useFieldsActions = (availableFields?: BusinessIssueField[] | null) => {
	const [fields, setFields, setFieldsOld] = useFieldsPreference();
	const [{ createIndex, createFields }] = useInlineFieldCreateControls();

	const resetCache = useCallback(() => {
		setFields(undefined);
		setFieldsOld(undefined);
	}, [setFields, setFieldsOld]);

	const reorderFields = useCallback<ReorderFields>(
		({ visibleFields, oldIndex, newIndex }: ReorderFieldsProps) => {
			try {
				if (oldIndex === newIndex) {
					return;
				}

				let oldFieldIndex = oldIndex;
				let newFieldIndex = newIndex;

				if (newFieldIndex < 3) {
					/*
                    moving column to:
                    - the first position it's not possible due to the bulk selection
                    - the second position it's not possible because the type issue should not be repositioned
                    - the third position it's not possible because the key issue should not be repositioned
                */
					return;
				}

				// We have an extra column at the beginning for bulk selection
				// that should be considered to reorder fields
				oldFieldIndex -= 1;
				newFieldIndex -= 1;

				const reorderedFields = [...visibleFields];
				const [reorderedField] = reorderedFields.splice(oldFieldIndex, 1);
				reorderedFields.splice(newFieldIndex, 0, reorderedField);
				// visibleFields is initially set by DEFAULT_VISIBLE_FIELDS which are type based
				// we need to keep it consistent for those fields as getAvailableFields finds this field by type
				const fieldsOrder = reorderedFields.map((field) =>
					field?.type && DEFAULT_CUSTOM_FIELDS_BY_TYPE.includes(field.type) ? field.type : field.id,
				);

				setFields({
					...fields,
					order: fieldsOrder,
				});
			} catch (error: unknown) {
				resetCache();
				fireErrorAnalytics({
					meta: {
						id: 'reorderFields',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: { message: 'Error trying to reorder the fields' },
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: error as Error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[fields, resetCache, setFields],
	);

	const toggleField = useCallback<ToggleField>(
		(fieldId: string) => {
			try {
				const isVisible = fields.order.includes(fieldId);
				const newFieldsOrder = [...fields.order];

				let createColumnIndex = createIndex;
				if (createFields) {
					const createField = createFields.find((f: CreateFieldsType) => f.fieldId === fieldId);
					createColumnIndex = createField?.createIndex ?? createColumnIndex;
				}

				if (!isVisible) {
					if (typeof createColumnIndex !== 'undefined') {
						newFieldsOrder.splice(createColumnIndex, 0, fieldId);
					} else {
						newFieldsOrder.push(fieldId);
					}
				} else {
					const fieldIndex = newFieldsOrder.findIndex((fId) => fId === fieldId);
					if (fieldIndex >= 0) {
						newFieldsOrder.splice(fieldIndex, 1);
					}
				}

				setFields({
					order: newFieldsOrder,
					details: getFieldsCache({
						availableFields,
						fields,
						order: newFieldsOrder,
					}),
				});
			} catch (error) {
				resetCache();
				fireErrorAnalytics({
					meta: {
						id: 'toggleFields',
						packageName: 'jiraBusinessList',
						teamName: 'keystone',
					},
					attributes: { message: 'Error trying to toogle a field' },
					// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
					error: error as Error,
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[fields, createIndex, createFields, setFields, availableFields, resetCache],
	);

	return { fields, toggleField, reorderFields, resetCache };
};
